import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import { X } from 'lucide-react'
import { Subtask } from 'constants/interfaces'
import { BaseModalV3, Button } from 'components/ui'
import { isRtlSelector } from 'redux/selectors/general'
import { useCourseHost } from '../../hooks/useCourseHost'
import { useExecutionText } from '../../hooks/useExecutionText'
import { courseViewerUrl } from '../../model/courseBridge'
import styles from './styles.module.css'

interface IExecutionCourseViewerProps {
  subtask: Subtask
  onClose: () => void
}

/**
 * A SCORM course in a full-screen viewer over the task. The course page gets only its own url (no token,
 * ids or names); useCourseHost answers its messages and saves the progress.
 */
export function ExecutionCourseViewer({ subtask, onClose }: IExecutionCourseViewerProps) {
  const isRtl = useSelector(isRtlSelector)
  const t = useExecutionText()

  const frameRef = useRef<HTMLIFrameElement>(null)

  const host = useCourseHost(subtask, frameRef, onClose)

  return (
    <BaseModalV3
      isOpen
      onClose={host.requestClose}
      closeOnEsc
      // The task modal (Chakra) already locks focus, and its react-focus-lock is a separate copy (the cjs build)
      // from BaseModalV3's, so a second lock here fights it. A cross-origin iframe gives no React focus events, so
      // the viewer must also stay inside the task modal's DOM (no portal) for that lock to let the course keep focus.
      usePortal={false}
      contentProps={{
        className: styles.viewer,
        dir: isRtl ? 'rtl' : 'ltr',
        'aria-label': subtask.title,
        style: { width: '100%', maxWidth: 'none', minWidth: 0, height: '100%', borderRadius: 0 },
      }}
      headerProps={{ className: styles.header }}
      header={
        <>
          <span className={styles.title}>{subtask.title}</span>
          <Button
            variant="icon"
            onClick={host.requestClose}
            isLoading={host.closing}
            isDisabled={host.closing}
            aria-label={t.close}
            title={t.close}
          >
            <X size={22} />
          </Button>
        </>
      }
      bodyProps={{ style: { padding: 0, maxHeight: 'none', height: '100%', overflow: 'hidden' } }}
      // the course uses the whole height: no footer
      footer={null}
      footerProps={{ style: { display: 'none' } }}
      body={
        <iframe
          ref={frameRef}
          className={styles.frame}
          src={courseViewerUrl(subtask.external_link!)}
          title={subtask.title}
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      }
    />
  )
}
